if (!customElements.get('fvb-product-tile-action')) {
  class FvbProductTileAction extends HTMLElement {
    constructor() {
      super();
      this.onUpdate = this.update.bind(this);
      this.onSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      const isStandalone = this.hasAttribute('data-standalone');
      this.form = isStandalone ? null : this.closest('form');
      if (!isStandalone && !this.form) return;

      this.variantInput = this.querySelector('[name="id"], [data-cart-field-name="id"]');
      this.submitButton = this.querySelector('[name="add"]');
      this.priceWrapper = this.querySelector('.product-tile-action__price');

      const variantsId = this.dataset.variantsId;
      const variantsEl = variantsId ? document.getElementById(variantsId) : null;
      this.variants = variantsEl ? JSON.parse(variantsEl.textContent || '[]') : [];

      if (this.form) {
        this.form.addEventListener('change', this.onUpdate);
        this.form.addEventListener('input', this.onUpdate);
        this.form.addEventListener('submit', this.onSubmit);
      }
      this.addEventListener('change', this.onUpdate);
      this.addEventListener('input', this.onUpdate);
      if (isStandalone && this.submitButton) {
        this.submitButton.addEventListener('click', this.onSubmit);
      }
      ['pointerdown', 'mousedown', 'touchstart'].forEach(eventName => {
        this.addEventListener(eventName, event => event.stopPropagation());
      });
      // Ensure button is never disabled by default — clicking without a size
      // selection shows an error instead (handled in handleSubmit).
      if (this.submitButton) {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
      }
      this.update();
      this.initialized = true;
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('change', this.onUpdate);
        this.form.removeEventListener('input', this.onUpdate);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      this.removeEventListener('change', this.onUpdate);
      this.removeEventListener('input', this.onUpdate);
      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.onSubmit);
      }
      this.initialized = false;
    }

    handleSubmit(evt) {
      const isStandalone = this.hasAttribute('data-standalone');
      if (!this.submitButton) this.submitButton = this.querySelector('[name="add"]');
      if (!this.variantInput) {
        this.variantInput = this.querySelector('[name="id"], [data-cart-field-name="id"]');
      }
      if (!isStandalone && (!this.form || !this.submitButton)) return;
      if (!this.submitButton) return;

      // In form mode, only handle submit triggered by this button
      if (!isStandalone && evt.submitter && evt.submitter !== this.submitButton) return;

      // Prevent the parent form (e.g. cart form) from submitting
      evt.preventDefault();

      if (this.submitButton.hasAttribute('disabled') || this.submitButton.getAttribute('aria-disabled') === 'true') return;

      const spinner = this.submitButton.querySelector('.loading__spinner');
      const errorContainer = isStandalone ? this.parentElement : this.form;
      const errorWrapper = errorContainer ? errorContainer.querySelector('.product-form__error-message-wrapper') : null;
      const errorMessage = errorContainer ? errorContainer.querySelector('.product-form__error-message') : null;
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

      if (this.variantInput && !this.variantInput.value) {
        const customSelect = this.querySelector('custom-variant-select');
        if (customSelect) customSelect.classList.add('error');
        return;
      }

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('loading');
      if (spinner) spinner.classList.remove('hidden');
      if (errorWrapper) errorWrapper.setAttribute('hidden', '');

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const cartItemsEl = document.querySelector('cart-items');

      let formData;
      if (isStandalone) {
        formData = new FormData();
        this.querySelectorAll('input[name], select[name], input[data-cart-field-name], select[data-cart-field-name]')
          .forEach(input => {
            const fieldName = input.dataset.cartFieldName || input.name;
            if (fieldName) formData.append(fieldName, input.value);
          });
        if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function') {
          formData.append('sections', cartItemsEl.getSectionsToRender().map(s => s.section).join(','));
          formData.append('sections_url', window.location.pathname);
        }
      } else {
        formData = new FormData(this.form);
      }
      if (!isStandalone && cart) {
        formData.append('sections', cart.getSectionsToRender().map(section => section.section).join(','));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }
      config.body = formData;

      fetch(routes.cart_add_url, config)
        .then(response => {
          const contentType = response.headers.get('content-type') || '';
          if (contentType.indexOf('application/json') !== -1 || contentType.indexOf('text/javascript') !== -1) {
            return response.json();
          }
          return response.text().then(() => {
            throw new Error('Non-JSON response from cart/add.');
          });
        })
        .then(response => {
          if (response.status) {
            if (errorMessage) errorMessage.textContent = response.description || response.message;
            if (errorWrapper) errorWrapper.removeAttribute('hidden');
            return;
          }

          if (isStandalone) {
            if (cartItemsEl && typeof cartItemsEl.getSectionsToRender === 'function' && response.sections) {
              cartItemsEl.getSectionsToRender().forEach(section => {

                const el = document.querySelector(section.selector) || document.querySelector(section.id);
                if (el && response.sections[section.section]) {
                  el.innerHTML = cartItemsEl.getSectionInnerHTML(response.sections[section.section], section.selector);
                }
              });
            } else {
              window.location.reload();
            }
          } else if (cart) {
            cart.renderContents(response);
          } else {
            window.location = window.routes.cart_url;
          }
        })
        .catch(error => {
          console.error(error);
          if (errorMessage) errorMessage.textContent = window.cartStrings?.error || error.message;
          if (errorWrapper) errorWrapper.removeAttribute('hidden');
        })
        .finally(() => {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    getSelectedValues() {
      const selects = this.querySelectorAll(
        '[data-product-tile-option-select], select[name^="product-tile-action-size-picker-"], select[name^="size-picker-"]'
      );
      return Array.from(selects).map(select => select.value);
    }

    findMatchingVariant(selectedValues) {
      if (!this.variants?.length) return null;

      if (this.variantInput?.value) {
        return this.variants.find(variant => String(variant.id) === String(this.variantInput.value)) || null;
      }

      return null;
    }

    updateButtonState(variant) {
      if (!this.submitButton) return;

      // Only disable when a variant is explicitly unavailable (sold out).
      // null means no size selected yet — button stays active so the user
      // can click and receive a "please select a size" error message.
      if (variant && !variant.available) {
        this.submitButton.setAttribute('disabled', 'disabled');
        this.submitButton.setAttribute('aria-disabled', 'true');
      } else {
        this.submitButton.removeAttribute('disabled');
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    updatePrice(variant) {
      if (!this.priceWrapper || !variant) return;

      const priceRoot = this.priceWrapper.querySelector('.price');
      const priceContainer = this.priceWrapper.querySelector('.price__container');
      if (!priceRoot || !priceContainer) return;

      const moneyFormat = priceContainer.dataset.moneyFormat;
      const labelPriceRegular = priceContainer.dataset.labelPriceRegular;
      const labelPriceSale = priceContainer.dataset.labelPriceSale;
      const labelPriceUnit = this.dataset.labelPriceUnit || '';

      const formatter = typeof window.formatPrice === 'function'
        ? window.formatPrice
        : function (amount) { return amount; };

      const renderUnitPrice = () => {
        if (!variant.unit_price_measurement || !variant.unit_price) return '';

        return [
          '<div class="product__notice caption caption--small">',
            labelPriceUnit ? '<span class="visually-hidden">' + labelPriceUnit + '</span>' : '',
            formatter(variant.unit_price, moneyFormat),
            '<span aria-hidden="true">/</span>',
            variant.unit_price_measurement.reference_value !== 1 ? variant.unit_price_measurement.reference_value : '',
            variant.unit_price_measurement.reference_unit || '',
          '</div>'
        ].join('');
      };

      const isOnSale = Boolean(variant.compare_at_price && variant.compare_at_price > variant.price);
      priceRoot.classList.toggle('price--sold-out', !variant.available);
      priceRoot.classList.toggle('price--on-sale', isOnSale);

      priceContainer.dataset.price = variant.price;
      priceContainer.dataset.compareAtPrice = variant.compare_at_price || '';

      if (isOnSale) {
        priceContainer.innerHTML = [
          '<div class="price__sale">',
            '<div class="price__sale-inner">',
              '<ins>',
                '<span class="visually-hidden">', labelPriceSale, '</span>',
                formatter(variant.price, moneyFormat),
              '</ins>',
              '<span class="visually-hidden">', labelPriceRegular, '</span>',
              '<s>', formatter(variant.compare_at_price, moneyFormat), '</s>',
            '</div>',
          '</div>',
          renderUnitPrice()
        ].join('');
        return;
      }

      priceContainer.innerHTML = [
        '<div class="price__regular">',
          '<span class="visually-hidden">', labelPriceRegular, '</span>',
          '<span>', formatter(variant.price, moneyFormat), '</span>',
        '</div>',
        renderUnitPrice()
      ].join('');
    }

    update() {
      const selectedValues = this.getSelectedValues();
      const match = this.findMatchingVariant(selectedValues);

      if (match && this.variantInput) {
        this.variantInput.value = match.id;
      }

      this.updateButtonState(match);
      this.updatePrice(match);
    }
  }

  customElements.define('fvb-product-tile-action', FvbProductTileAction);
}

document.addEventListener('click', function(evt) {
  const btn = evt.target.closest('fvb-product-tile-action[data-standalone] [name="add"]');
  if (!btn) return;
  const tileAction = btn.closest('fvb-product-tile-action');
  if (tileAction && typeof tileAction.handleSubmit === 'function') {
    tileAction.handleSubmit(evt);
  }
});
